// PNG decode + auto-palette indexing — the ONE implementation both
// tiers run, so the same .munos source paints byte-for-byte the same
// indexed pixels everywhere. (This used to exist twice: pngjs-based in
// the server's png-decode.ts, fast-png-based in the browser host. The
// indexing rules were mirrored by hand; now they can't drift.)
//
// Output:
//   indexed:  one byte per pixel, palette index (0 = transparent).
//   palette:  Uint32Array, slot 0 = 0x00000000 (transparent), slots
//             1..N = opaque colors as 0xFFRRGGBB. Opaque colors are
//             dedup'd in scan order, then sorted by Rec. 709 luminance
//             with a deterministic 0xRRGGBB tiebreak.
import { decode as fastPngDecode } from "fast-png";
// Decode PNG bytes and derive the indexed-pixel + auto-palette form.
// Throws on malformed PNG bytes (callers wrap with their own
// diagnostics — the byte stream usually came out of script memory and
// the useful context lives at the call site).
export function decodePngToIndexed(bytes) {
    const decoded = fastPngDecode(bytes);
    return indexDecodedPng(decoded);
}
// Convert a decode result into indexed pixels + auto palette.
// Normalizes to 8-bit RGBA regardless of source depth/channels: 16-bit
// PNGs get truncated to the high byte; gray / gray+alpha / RGB expand
// to RGBA. (1/2/4-bit grayscale isn't expected for sprite art and
// would need different bit-unpacking.)
export function indexDecodedPng(decoded) {
    const w = decoded.width;
    const h = decoded.height;
    const channels = decoded.channels ?? 4;
    const depth = decoded.depth ?? 8;
    const src = decoded.data;
    const sample = (i) => {
        if (depth === 16)
            return src[i] >> 8;
        return src[i];
    };
    let getRGBA;
    if (channels === 4) {
        getRGBA = (px) => [sample(px * 4), sample(px * 4 + 1), sample(px * 4 + 2), sample(px * 4 + 3)];
    }
    else if (channels === 3) {
        getRGBA = (px) => [sample(px * 3), sample(px * 3 + 1), sample(px * 3 + 2), 255];
    }
    else if (channels === 2) {
        // gray + alpha
        getRGBA = (px) => { const g = sample(px * 2); return [g, g, g, sample(px * 2 + 1)]; };
    }
    else {
        // grayscale
        getRGBA = (px) => { const g = sample(px); return [g, g, g, 255]; };
    }
    const seen = new Set();
    const opaqueColors = [];
    for (let i = 0; i < w * h; i++) {
        const [r, g, b, a] = getRGBA(i);
        if (a < 128)
            continue;
        const key = (r << 16) | (g << 8) | b;
        if (seen.has(key))
            continue;
        seen.add(key);
        opaqueColors.push(key);
    }
    // Index 0 is reserved for transparent, so opaque colors occupy indices
    // 1..255 — a u8 can address at most 255 distinct opaque colors. Past that
    // the index assigned below would wrap in the Uint8Array and silently
    // mis-map colors (index 256 → 0 = transparent). Fail loudly instead:
    // sprite art is far under this, and a photographic/gradient PNG isn't
    // valid indexed-sprite input.
    if (opaqueColors.length > 255) {
        throw new Error(`image has ${opaqueColors.length} distinct opaque colors; the indexed ` +
            `sprite format supports at most 255 (index 0 is reserved for transparent)`);
    }
    const luminance = (rgb) => {
        const r = (rgb >> 16) & 0xff;
        const g = (rgb >> 8) & 0xff;
        const bl = rgb & 0xff;
        return 0.2126 * r + 0.7152 * g + 0.0722 * bl;
    };
    opaqueColors.sort((a, b) => {
        const la = luminance(a), lb = luminance(b);
        if (la !== lb)
            return la - lb;
        return a - b;
    });
    const palette = [0x00000000];
    const colorToIndex = new Map();
    for (const key of opaqueColors) {
        colorToIndex.set(key, palette.length);
        palette.push(((0xff << 24) | key) >>> 0);
    }
    const indexed = new Uint8Array(w * h);
    for (let i = 0; i < w * h; i++) {
        const [r, g, b, a] = getRGBA(i);
        if (a < 128) {
            indexed[i] = 0;
            continue;
        }
        const key = (r << 16) | (g << 8) | b;
        indexed[i] = colorToIndex.get(key) ?? 0;
    }
    return { width: w, height: h, indexed, palette: Uint32Array.from(palette) };
}
// Copy the (x, y, w, h) sub-rectangle out of a decoded sheet's indexed
// pixels. Out-of-bounds source rows/cols stay 0 (transparent) — the
// behavior both tiers always had for sprites cut near a sheet edge.
export function extractIndexedRegion(png, x, y, w, h) {
    const pixels = new Uint8Array(w * h);
    for (let dy = 0; dy < h; dy++) {
        const sy = y + dy;
        if (sy < 0 || sy >= png.height)
            continue;
        for (let dx = 0; dx < w; dx++) {
            const sx = x + dx;
            if (sx < 0 || sx >= png.width)
                continue;
            pixels[dy * w + dx] = png.indexed[sy * png.width + sx] ?? 0;
        }
    }
    return pixels;
}
//# sourceMappingURL=png-index.js.map